import { Aide } from '@/components/ui/aide';
import ShortageFinancialActions from './ShortageFinancialActions';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, durableBusinessPost } from '../lib/api';
import { messageErreur } from '../lib/erreurs';
import { libelle, referenceLisible, texteSur } from '../lib/libelles';
import { accorde, formatDate, formatDateHeure, formatLitres } from '../lib/formats';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { BarrePagination } from './ui/pagination-bar';
const CODES_DOSSIER = ['UNKNOWN', 'IN_PROGRESS', 'WITHIN_TOLERANCE', 'OUTSIDE_TOLERANCE', 'SURPLUS', 'TO_REVIEW', 'CONTESTED', 'COMPANY_ACCEPTED', 'ASSIGNED', 'SHARED', 'RECOVERY_IN_PROGRESS', 'RESOLVED'];
export const shortageLabels = Object.fromEntries(CODES_DOSSIER.map(code => [code, libelle('shortageCase', code)]));
const etatDossier = code => libelle('shortageCase', code, 'À examiner');
const volumes = {
  loaded_volume: 'Chargé',
  delivered_volume: 'Livré',
  returned_volume: 'Rendu',
  remaining_in_truck: 'Resté dans le camion'
};
const FORMULE = 'Manquant = chargé − livré − rendu − resté dans le camion';
const libelleJustificatif = document => {
  const type = document.doc_type ? libelle('documentType', document.doc_type, '') : '';
  const date = document.document_date || document.issue_date || document.created_at;
  const morceaux = [type || document.title || 'Justificatif', date ? formatDate(date) : ''].filter(Boolean);
  return morceaux.join(' · ');
};
const key = () => `shortage-${crypto.randomUUID()}`;
// Un trajet a toujours un camion: le serveur décrit chaque dossier ou écart par
// son trajet (convoi, commande, plaques figées au départ, chauffeur, client) et
// écarte ceux dont le trajet n'existe plus.
export const referenceTrajet = row => row?.convoy_code ? `Convoi ${row.convoy_code}` : row?.order_reference ? `Commande ${row.order_reference}` : `Trajet du ${formatDate(row?.trip_date || row?.created_at, 'jour non précisé')}`;
export const plaquesTrajet = row => [row?.tractor_plate || row?.truck_matricule, row?.tank_plate].filter(Boolean).join(' / ');
export const libelleTrajet = row => [referenceTrajet(row), plaquesTrajet(row)].filter(Boolean).join(' · ');
export default function ShortagePanel({
  tripId,
  driverId,
  repliable = false
}) {
  const {
    user
  } = useAuth();
  const driver = user?.role === 'CHAUFFEUR';
  const [rows, setRows] = useState([]),
    [selected, setSelected] = useState(tripId || ''),
    [detail, setDetail] = useState(null);
  // La liste se lit par pages et se cherche côté serveur: le total et le
  // manquant constaté portent sur tous les dossiers retenus, pas sur la page.
  const [total, setTotal] = useState(0),
    [totalLiters, setTotalLiters] = useState(0),
    [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const pagination = usePaginationServeur(total, {
    cle: 'dossiers',
    reinitialiser: `${debouncedSearch}|${driverId || ''}`
  });
  const {
    page: pageNumber,
    limit: pageSize
  } = pagination;
  const [notice, setNotice] = useState('');
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [comment, setComment] = useState('');
  const [resources, setResources] = useState({
      documents: [],
      movements: []
    }),
    [documents, setDocuments] = useState([]),
    [movement, setMovement] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [measure, setMeasure] = useState({}),
    [status, setStatus] = useState('TO_REVIEW'),
    [eventKey, setEventKey] = useState(key);
  const nouvelleDemande = useDerniereDemande();
  const load = useCallback(async () => {
    const perimee = nouvelleDemande();
    try {
      if (selected) {
        const response = await api.get(`/shortages/trips/${selected}`);
        if (perimee()) return;
        const value = response.data.data;
        setDetail(value);
        const resources = await api.get(`/shortages/trips/${selected}/resources`);
        if (perimee()) return;
        setResources(resources.data.data);
        setMeasure(Object.fromEntries(Object.keys(volumes).map(name => [name, value.case?.[name] ?? ''])));
      } else {
        const response = await api.get('/shortages', {
          params: {
            driver_id: driverId,
            q: debouncedSearch.trim() || undefined,
            page: pageNumber,
            page_size: pageSize
          }
        });
        if (perimee()) return;
        const lignes = response.data.data || [];
        const meta = response.data.meta || {};
        const enTete = Number.parseInt(response.headers?.['x-total-count'], 10);
        setRows(lignes);
        setTotal(Number.isNaN(enTete) ? meta.total ?? lignes.length : enTete);
        setTotalLiters(meta.total_shortage_liters ?? lignes.reduce((sum, row) => sum + Number(row.shortage_liters || 0), 0));
      }
      setError('');
    } catch (e) {
      if (perimee()) return;
      setError(messageErreur(e, 'Action impossible pour le moment. Réessayez.'));
    }
  }, [selected, driverId, debouncedSearch, pageNumber, pageSize, nouvelleDemande]);
  useEffect(() => {
    load();
  }, [load]);
  useRafraichirSur(['trajets', 'stock', 'finance', 'depenses'], load);
  const submit = async eventType => {
    setBusy(true);
    setError('');
    try {
      const data = {
        idempotency_key: eventKey,
        expected_revision: detail?.case?.measurement_revision || 0,
        event_type: eventType,
        comment,
        document_ids: documents,
        source: 'web'
      };
      if (eventType === 'MEASUREMENT') {
        Object.keys(volumes).forEach(name => {
          if (measure[name] !== '') data[name] = Number(measure[name]);
        });
        data.measurement_final = !!measure.measurement_final;
      }
      if (eventType === 'DECISION') data.case_status = status;
      await durableBusinessPost('shortage_event', 'create', `/shortages/trips/${selected}/events`, data, {
        trip_id: selected
      });
      setNotice('Action confirmée dans le dossier.');
      setComment('');
      setEventKey(key());
      await load();
    } catch (e) {
      setError(messageErreur(e, 'Action impossible pour le moment. Réessayez.'));
    } finally {
      setBusy(false);
    }
  };
  // Sur la fiche d'un trajet (`repliable`), le panneau est replié: l'opérateur n'a besoin
  // du dossier que s'il y a un écart à comprendre. La liste globale reste ouverte.
  const pastille = detail?.case ? etatDossier(detail.case.case_status) : 'Aucun constat';
  const corps = <>
    <div><Aide libelle="Bilan de fin de trajet" titre="Bilan de fin de trajet">Bilan de fin de trajet : ce qui a été chargé comparé à ce qui a été livré, rendu ou est resté dans le camion. Tolérance par trajet : 100 L, sauf règle de transport propre à la société ; elle est figée à l’ouverture du dossier. Le chauffeur n’est pas présumé responsable.</Aide></div>
    {notice && <p role="status" className="text-sm">{notice}</p>}
    {error && <p role="alert" className="text-destructive">{error}</p>}
    {!selected ? <>{(total > 0 || search) && <><p className="text-sm">{accorde(total, 'dossier')} · manquant constaté au total : {formatLitres(totalLiters)}</p>
      <Input aria-label="Filtrer les dossiers" placeholder="Trajet, camion, traitement…" value={search} onChange={event => setSearch(event.target.value)} /></>}
      <div className="divide-y">{rows.map(row => <button key={row.id} className="w-full py-3 text-left flex justify-between gap-4" onClick={() => setSelected(row.reference_id)}><span><span className="block">{libelleTrajet(row)} · {etatDossier(row.case_status)}</span><span className="block text-xs text-muted-foreground">{row.driver_name || 'Chauffeur non renseigné'} · {row.client_name || 'Client à préciser'}</span></span><span className="shrink-0 tabular-nums">{row.shortage_liters == null ? 'Bilan à compléter' : formatLitres(row.shortage_liters)}</span></button>)}{!rows.length && <p className="flex items-center gap-1 py-3 text-muted-foreground">Aucun dossier de manquant.<Aide titre="Dossiers de manquant">Un dossier s’ouvre quand le bilan d’un trajet est saisi.</Aide></p>}</div>{rows.length > 0 && <BarrePagination pagination={pagination} nom="dossiers" />}</> : detail && <>
      <div className="grid gap-3 sm:grid-cols-4">{Object.entries(volumes).map(([name, label]) => <div key={name}><p className="text-xs text-muted-foreground">{label}</p><strong>{detail.case?.[name] == null ? 'Non saisi' : `${Number(detail.case[name]).toLocaleString('fr-FR')} L`}</strong></div>)}</div>
      <Aide titre="Calcul du manquant">{FORMULE}</Aide>
      <p><strong>{detail.metrics?.classification ? libelle('shortageCase', detail.metrics.classification, 'Mesures non saisies') : 'Mesures non saisies'}</strong> · {detail.metrics?.shortage_liters == null ? 'Manquant non établi' : `Manquant ${formatLitres(detail.metrics.shortage_liters)}`} · {etatDossier(detail.case?.case_status)}</p>
      <p className="text-xs text-muted-foreground">Tolérance appliquée à ce trajet : {formatLitres(detail.case?.tolerance_snapshot?.liters ?? 100)}. {detail.case?.financial_decision ? 'Décision enregistrée ; les opérations financières sont distinctes.' : 'Aucune responsabilité ni dette créée.'}</p>
      <Link className="text-primary underline text-sm" to={`/trips/${selected}`}>Ouvrir le trajet</Link>
      {!driver && <fieldset className="rounded-lg border p-3 space-y-3"><legend className="px-2 text-sm">Saisir ou corriger les volumes</legend><div className="grid sm:grid-cols-4 gap-3">{Object.entries(volumes).map(([name, label]) => <label className="text-sm" key={name}>{label} (L)<Input type="number" min="0" step="0.001" value={measure[name] ?? ''} onChange={event => {
              setMeasure({
                ...measure,
                [name]: event.target.value
              });
              setEventKey(key());
            }} /></label>)}</div><label className="flex gap-2 text-sm"><input type="checkbox" checked={!!measure.measurement_final} onChange={event => {
            setMeasure({
              ...measure,
              measurement_final: event.target.checked
            });
            setEventKey(key());
          }} />Bilan final du trajet : retours et reste vérifiés</label><Button disabled={busy} onClick={() => submit('MEASUREMENT')}>Enregistrer le constat</Button><Aide titre="Calcul du manquant">{FORMULE}</Aide></fieldset>}
      <label className="block text-sm">Justificatifs du trajet<select multiple className="block w-full rounded-md border bg-background p-2" value={documents} onChange={event => {
          setDocuments([...event.target.selectedOptions].map(option => option.value));
          setEventKey(key());
        }}>{resources.documents.map(document => <option value={document.id} key={document.id}>{libelleJustificatif(document)}</option>)}</select></label><Link className="text-sm text-primary underline" to="/documents">Ajouter un justificatif</Link>
      <label className="block text-sm">Explication ou motif<textarea className="mt-1 w-full rounded-md border bg-background p-2" value={comment} onChange={event => {
          setComment(event.target.value);
          setEventKey(key());
        }} /></label>
      <div className="space-y-2 rounded-lg border p-3" data-testid="manquants-decision">
        <div className="flex items-center gap-1">
          <h4 className="text-sm font-semibold">Décision</h4>
          <Aide titre="Décision">
            <p>Écrivez d’abord le motif ci-dessus, puis choisissez :</p>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li><strong className="text-foreground">Expliquer</strong> : ajoute votre explication au dossier, sans rien changer aux volumes.</li>
              <li><strong className="text-foreground">Contester</strong> : signale que vous n’êtes pas d’accord avec les volumes saisis.</li>
              {!driver && <li><strong className="text-foreground">Décider</strong> : fixe le traitement du dossier choisi dans la liste.</li>}
            </ul>
          </Aide>
        </div>
        <div className="flex flex-wrap gap-2"><Button variant="outline" disabled={busy || !comment.trim()} onClick={() => submit('EXPLANATION')}>Ajouter une explication</Button><Button variant="outline" disabled={busy || !comment.trim()} onClick={() => submit('CONTEST')}>Contester la mesure</Button>
        {!driver && <><select aria-label="Traitement du dossier" className="rounded-md border bg-background p-2" value={status} onChange={event => {
            setStatus(event.target.value);
            setEventKey(key());
          }}>{['TO_REVIEW', 'WITHIN_TOLERANCE', 'CONTESTED', 'COMPANY_ACCEPTED', 'ASSIGNED', 'SHARED', 'RECOVERY_IN_PROGRESS', 'RESOLVED'].map(value => <option key={value} value={value}>{libelle('shortageCase', value)}</option>)}</select><Button disabled={busy || !comment.trim()} onClick={() => submit('DECISION')}>Enregistrer la décision</Button></>}
        </div>
      </div>
      {!driver && detail.case && <ShortageFinancialActions detail={detail} onChanged={load} />}
      {!driver && detail.case && <details className="rounded-lg border p-3"><summary className="cursor-pointer">Rattacher l’effet stock déjà enregistré</summary><div className="py-2"><Aide titre="Rattachement" libelle="Effet sur le stock">Ce rattachement ne crée aucun mouvement supplémentaire. Un ajustement justifié se saisit dans Stock après vérification des mouvements existants.</Aide></div><select aria-label="Mouvement de stock du trajet" className="rounded-md border bg-background p-2 w-full" value={movement} onChange={event => {
          setMovement(event.target.value);
          setEventKey(key());
        }}><option value="">Choisir un mouvement existant</option>{resources.movements.map(row => <option key={row.id} value={row.id}>{libelle('stockMovement', row.movement_type)} · {Number(row.quantity_liters || 0).toLocaleString('fr-FR')} L · {referenceLisible(row.reference_number || row.source, 'Sans référence')}</option>)}</select><Button className="mt-3" variant="outline" disabled={busy || !movement || !comment.trim()} onClick={async () => {
          setBusy(true);
          try {
            await api.post(`/shortages/trips/${selected}/stock-link`, {
              movement_id: movement,
              reason: comment,
              idempotency_key: eventKey
            });
            setEventKey(key());
            await load();
          } catch (e) {
            setError(messageErreur(e, 'Action impossible pour le moment. Réessayez.'));
          } finally {
            setBusy(false);
          }
        }}>Rattacher avec le motif saisi</Button></details>}
      <ol className="border-l pl-4 space-y-3">{detail.events.map(event => <li key={event.id} className="text-sm"><p className="font-medium">{libelle('shortageEvent', event.event_type)} · version {event.revision}</p><p className="text-muted-foreground">{event.created_at && formatDateHeure(event.created_at)} · {texteSur(event.details?.input?.comment || event.details?.reason, '')}</p>{event.details?.before && event.event_type === 'MEASUREMENT' && <details><summary>Mesures précédentes</summary>{Object.entries(volumes).map(([name, label]) => <p key={name}>{label} : {event.details.before[name] == null ? 'Non renseigné' : formatLitres(event.details.before[name])}</p>)}</details>}</li>)}</ol>
    </>}
  </>;
  if (!repliable) return <section className="rounded-xl border bg-card p-5 space-y-4" aria-label="Suivi des manquants">
    <div className="flex items-center justify-between gap-3"><h3 className="font-semibold">Manquants de carburant</h3>{selected && <Button variant="outline" onClick={() => {
        setSelected('');
        setDetail(null);
      }}>Tous les dossiers</Button>}</div>
    {corps}
  </section>;
  return <Collapsible open={ouvert} onOpenChange={setOuvert} asChild>
    <section className="rounded-xl border bg-card" aria-label="Suivi des manquants">
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-5 py-3 text-left" data-testid="manquants-trigger">
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${ouvert ? 'rotate-180' : ''}`} aria-hidden="true" />
        <h3 className="flex-1 font-semibold">Manquants de carburant</h3>
        <span className="rounded-full border px-2.5 py-0.5 text-xs font-medium">{pastille}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 border-t px-5 py-4">{corps}</CollapsibleContent>
    </section>
  </Collapsible>;
}
